import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { ApiService } from '../../../services/api';
import { getModeCanonicalKey, type ModeConfig } from './speedReadingShared';

// Öğrencinin açtığı her uygulama sunucuda bir kayıt olur (yönetici "Öğrenci Raporu" buradan beslenir):
// açılış, sekme görünürken geçen süre ve uygulama bildirirse sonuç (okuma hızı, anlama soruları).

export type SpeedReadingActivityResult = {
  wordCount?: number;
  wpm?: number;
  questionCount?: number;
  correctCount?: number;
  contentId?: string | null;
};

const TICK_MS = 15000;
// Süre ~30 sn'de bir yazılır; sekme kapanırsa en fazla bu kadarı kaybolur.
const SYNC_EVERY_TICKS = 2;

const SpeedReadingActivityContext = createContext<(result: SpeedReadingActivityResult) => void>(() => undefined);

export const useReportSpeedReadingResult = () => useContext(SpeedReadingActivityContext);

export const SpeedReadingActivityTracker: React.FC<{ mode: ModeConfig; children: React.ReactNode }> = ({ mode, children }) => {
  const activityIdRef = useRef<string | null>(null);
  const activeMsRef = useRef(0);
  const lastTickRef = useRef(Date.now());
  const wasVisibleRef = useRef(true);
  const pendingRef = useRef<SpeedReadingActivityResult>({});

  // Yalnızca sekme görünürken geçen süre sayılır; cihaz uykuya geçtiyse aradaki boşluk sayılmaz.
  const tick = useCallback(() => {
    const now = Date.now();
    if (wasVisibleRef.current) activeMsRef.current += Math.min(now - lastTickRef.current, TICK_MS * 2);
    lastTickRef.current = now;
    wasVisibleRef.current = typeof document === 'undefined' || document.visibilityState === 'visible';
  }, []);

  const sync = useCallback((ended = false) => {
    const activityId = activityIdRef.current;
    if (!activityId) return;
    const payload = {
      activeSeconds: Math.round(activeMsRef.current / 1000),
      ...pendingRef.current,
      ...(ended ? { ended: true } : {}),
    };
    pendingRef.current = {};
    ApiService.patchApi(`speed-reading/activities/${activityId}`, payload).catch(() => undefined);
  }, []);

  useEffect(() => {
    let closed = false;
    lastTickRef.current = Date.now();
    wasVisibleRef.current = typeof document === 'undefined' || document.visibilityState === 'visible';

    ApiService.postApi('speed-reading/activities', {
      exerciseId: getModeCanonicalKey(mode) || mode.name,
      title: mode.name,
      sectionId: mode.sectionId || null,
    })
      .then((response: any) => {
        const activityId: string | null = response?.id || null;
        if (!activityId) return;
        // Uygulama yanıt gelmeden kapandıysa bu kayıt kendi numarasıyla kapatılır; ortak referans, hâlâ açık olan
        // (ör. geliştirme modunda yeniden kurulan) uygulamanın kaydına ait kalır.
        if (closed) {
          ApiService.patchApi(`speed-reading/activities/${activityId}`, { activeSeconds: 0, ended: true }).catch(() => undefined);
          return;
        }
        activityIdRef.current = activityId;
      })
      .catch(() => undefined);

    let ticks = 0;
    const timer = window.setInterval(() => {
      tick();
      ticks += 1;
      if (ticks % SYNC_EVERY_TICKS === 0) sync();
    }, TICK_MS);
    document.addEventListener('visibilitychange', tick);

    return () => {
      closed = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      tick();
      sync(true);
    };
    // Uygulama değişince bileşen yeniden kurulur (key), aynı uygulamada kayıt tek kalır.
  }, []);

  const reportResult = useCallback((result: SpeedReadingActivityResult) => {
    pendingRef.current = { ...pendingRef.current, ...result };
    tick();
    sync();
  }, [sync, tick]);

  return (
    <SpeedReadingActivityContext.Provider value={reportResult}>
      {children}
    </SpeedReadingActivityContext.Provider>
  );
};
